import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { FilmSlate, UploadSimple, ArrowsClockwise, DownloadSimple, CheckCircle, Eye, SpeakerHigh, Waveform } from "@phosphor-icons/react";
import { apiClient, API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const STAGES = ["analyzing", "generating", "mixing"];

export default function VideoFx({ credits, onCreditsSpent }) {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [hint, setHint] = useState("");
  const [shareUrl, setShareUrl] = useState(null);

  useEffect(() => {
    if (!job || job.status !== "processing") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await apiClient.get(`/v2a/jobs/${job.job_id}`);
        setJob(data);
        if (data.status === "error") toast.error(data.error || t("vfxErr"));
        if (data.status === "done") toast.success(t("vfxDone"));
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [job?.job_id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setJob(null); }
  };

  const start = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("video", file);
      const { data } = await apiClient.post("/v2a/jobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob({ job_id: data.job_id, status: "processing", stage: "analyzing", events: [] });
      onCreditsSpent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("vfxErr"));
    } finally {
      setUploading(false);
    }
  };

  const regen = async () => {
    try {
      const { data } = await apiClient.post(`/v2a/jobs/${job.job_id}/regenerate`, { hint: hint.trim() || null });
      setJob({ job_id: data.job_id, status: "processing", stage: "analyzing", events: [] });
      setShareUrl(null);
      setHint("");
      onCreditsSpent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("vfxErr"));
    }
  };

  const share = async () => {
    try {
      const { data } = await apiClient.post(`/v2a/jobs/${job.job_id}/share`);
      const url = `${window.location.origin}/api/vs/${data.share_id}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success(t("linkCopied")); }
      catch { toast.info(url, { duration: 10000 }); }
    } catch {
      toast.error(t("vfxErr"));
    }
  };

  const stageIdx = job ? STAGES.indexOf(job.stage) : -1;

  return (
    <div data-testid="video-fx">
      <p className="text-sm text-white/55 leading-relaxed mb-5">{t("vfxDesc")}</p>

      <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={pick} className="hidden" data-testid="vfx-file-input" />
      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="vfx-choose-btn"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors text-sm"
        >
          <UploadSimple size={17} weight="bold" /> {file ? file.name : t("vfxUpload")}
        </button>
        <button
          data-testid="vfx-generate-btn"
          onClick={start}
          disabled={!file || uploading || (job && job.status === "processing") || credits < 10}
          className="btn-primary px-5 py-3 rounded-xl font-medium inline-flex items-center gap-2 text-sm disabled:opacity-40"
        >
          {uploading ? <ArrowsClockwise size={17} className="animate-spin" /> : <FilmSlate size={17} weight="fill" />}
          {t("vfxGenerate")} · 10 {t("creditsShort")}
        </button>
      </div>
      <p className="text-[10px] font-mono-num uppercase tracking-widest text-white/30 mt-2">{t("vfxLimits")}</p>

      {job && job.status === "processing" && (
        <div data-testid="vfx-progress" className="mt-6 rounded-xl border border-white/10 bg-[color:var(--surface-2)] p-5 space-y-3">
          {[
            { id: "analyzing", icon: Eye, label: t("vfxAnalyzing") },
            { id: "generating", icon: SpeakerHigh, label: t("vfxGenerating") },
            { id: "mixing", icon: Waveform, label: t("vfxMixing") },
          ].map((s, i) => (
            <div key={s.id} className={`flex items-center gap-3 text-sm ${i <= stageIdx ? "text-white/90" : "text-white/30"}`}>
              {i < stageIdx ? (
                <CheckCircle size={18} weight="fill" className="text-emerald-400" />
              ) : i === stageIdx ? (
                <ArrowsClockwise size={18} className="animate-spin text-[color:var(--accent)]" />
              ) : (
                <s.icon size={18} />
              )}
              {s.label}
            </div>
          ))}
          {job.scene && <p className="text-xs text-white/45 pt-2 border-t border-white/5">🎬 {job.scene}</p>}
        </div>
      )}

      {job && job.status === "done" && (
        <div data-testid="vfx-result" className="mt-6 space-y-4">
          <video
            data-testid="vfx-video-player"
            controls
            src={`${API}/v2a/jobs/${job.job_id}/video`}
            className="w-full rounded-xl border border-white/10 bg-black max-h-[360px]"
          />
          {job.events?.length > 0 && (
            <div className="rounded-xl border border-white/10 bg-[color:var(--surface-2)] p-4">
              <p className="text-[10px] font-mono-num uppercase tracking-widest text-white/40 mb-2">{t("vfxEvents")}</p>
              <div className="space-y-1.5">
                {job.events.map((e, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <span className="font-mono-num text-[color:var(--accent)] w-12">{Number(e.time).toFixed(1)}s</span>
                    <span className="text-white/80">{e.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <a
            data-testid="vfx-download-btn"
            href={`${API}/v2a/jobs/${job.job_id}/video?download=true`}
            className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium"
          >
            <DownloadSimple size={16} weight="bold" /> {t("vfxDownload")}
          </a>

          <div className="rounded-xl border border-white/10 bg-[color:var(--surface-2)] p-4 space-y-3" data-testid="vfx-rework-panel">
            <p className="text-[10px] font-mono-num uppercase tracking-widest text-white/40">{t("vfxRegenTitle")}</p>
            <div className="flex gap-2">
              <input
                data-testid="vfx-hint-input"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                maxLength={300}
                placeholder={t("vfxRegenPh")}
                className="flex-1 bg-[color:var(--surface)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/25"
              />
              <button
                data-testid="vfx-regen-btn"
                onClick={regen}
                disabled={credits < 5}
                className="px-4 py-2.5 rounded-lg border border-[color:var(--accent)]/50 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 text-sm font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-40"
              >
                <ArrowsClockwise size={15} weight="bold" /> {t("vfxRegen")} · 5 {t("creditsShort")}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="vfx-share-row">
            <button
              data-testid="vfx-share-btn"
              onClick={share}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 text-sm transition-colors"
            >
              {t("vfxShare")}
            </button>
            {shareUrl && (
              <>
                <a data-testid="vfx-share-tg" target="_blank" rel="noreferrer" href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`}
                  className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs text-white/70">Telegram</a>
                <a data-testid="vfx-share-wa" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                  className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs text-white/70">WhatsApp</a>
                <a data-testid="vfx-share-x" target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
                  className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs text-white/70">X</a>
              </>
            )}
          </div>
        </div>
      )}

      {job && job.status === "error" && (
        <div data-testid="vfx-error" className="mt-6 rounded-xl border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/5 p-4 text-sm text-white/80">
          {job.error || t("vfxErr")}
        </div>
      )}
    </div>
  );
}
